import { Component, ChangeDetectionStrategy, input, effect, output } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Answer, Question } from 'shared';

import { interval, map, merge, Observable, takeUntil, tap, timer } from 'rxjs';
import { ExplanationComponent } from '../explanation/explanation.component';
import { CurrencyInputComponent } from '../currency-input/currency-input.component';

@Component({
  selector: 'app-question-viewer',
  templateUrl: './question-viewer.component.html',
  styleUrl: './question-viewer.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ExplanationComponent, CurrencyInputComponent],
})
export class QuestionViewerComponent {
  readonly question = input<Question | null | undefined>();

  readonly answerClicked = output<{
    answer?: Answer;
    time: number;
    givenExplanation?: string;
  }>();
  readonly showExplanationVideo = output<string>();

  givenAnswer?: { answer?: Answer; time: number; givenExplanation?: string };
  showExplanation = false;
  timeInPercentageForScaleX$: Observable<number | null> | undefined;
  timedOut: boolean = false;

  get isBudgetQuestion() {
    const q = this.question();
    return q && 'type' in q && q.type === 'budget';
  }

  constructor() {
    effect(() => {
      const question = this.question();

      const defaultTotalTimeInSec = 20;
      let totalTime = question?.questionTime ? question.questionTime : defaultTotalTimeInSec;
      const percentage = 100 / totalTime;

      const timerTimedOut = timer((totalTime + 3) * 1000 + 200).pipe(
        tap(() => {
          if (question && 'answers' in question) {
            const defaultAnswer = question.answers.find((answer) => answer.defaultAnswer);

            if (defaultAnswer) {
              this.onAnswerClicked(defaultAnswer);
            }
          }
        }),
      );

      this.timeInPercentageForScaleX$ = interval(100).pipe(
        takeUntil(merge(outputToObservable(this.answerClicked), timerTimedOut)),
        map((n) => Math.ceil(n * percentage) / 1000),
        tap((n) => {
          if (n === 1) {
            this.timedOut = true;
          }
        }),
        //Should never be triggered, but the 200ms thing is a bit tricky
        map((percentage) => (percentage > 1 ? 1 : percentage)),
      );
    });
  }

  onAnswerClicked(answer: Answer) {
    if (typeof answer.explanation === 'string') {
      this.showExplanation = true;
      this.givenAnswer = { answer, time: Date.now() };
    } else {
      this.answerClicked.emit({ answer, time: Date.now() });
    }
  }

  onExplanationSubmit($event: string) {
    this.showExplanation = false;
    if (this.givenAnswer) {
      if (this.givenAnswer.answer) this.givenAnswer.answer.explanation = this.givenAnswer.answer.label;
      this.answerClicked.emit({ ...this.givenAnswer, givenExplanation: $event });
    } else {
      this.answerClicked.emit({ time: Date.now(), givenExplanation: $event });
    }
  }
}
